import axios from 'axios';

const PLAN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

let cachedPlans = null;
let cacheTimestamp = 0;

const productIsActive = (plan) =>
  plan && plan.status && plan.status !== 'inactive' && plan.status !== '0';

const normalizeProducts = (products = []) =>
  products.map((product) => {
    const normalizeNumber = (value) =>
      typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

    return {
      id: product._id || product.id,
      name: product.name || 'Plan',
      description: product.description || '',
      billingCycle: (product.duration || 'monthly').toLowerCase(),
      pricingModel: product.price ? 'per-user' : 'flat',
      price: normalizeNumber(product.price) ?? normalizeNumber(product.monthlyPrice) ?? 0,
      currency: product.currency || 'USD',
      monthlyPrice: normalizeNumber(product.monthlyPrice),
      yearlyPrice: normalizeNumber(product.yearlyprice),
      trialPeriod: product.trial_period ?? null,
      status: `${product.status ?? ''}`.toLowerCase(),
      features: Array.isArray(product.features) ? product.features : [],
    };
  });

export const subscriptionService = {
  async getBillingPlans(token, forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && cachedPlans && now - cacheTimestamp < PLAN_CACHE_TTL) {
      return cachedPlans;
    }
//     const response = await apiClient.get('/api/billing/products', {
//       headers: token
//         ? {
//             Authorization: `Bearer ${token}`,
//           }
//         : undefined,
//     });

    const response = await axios.get("/api/billing/user-products", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });


    cachedPlans = normalizeProducts(response.data || []).filter(
      (plan) =>
        (plan.status === 'active' || plan.status === '1') &&
        productIsActive(plan)
    );
    cacheTimestamp = now;
    return cachedPlans;
  },
  clearPlanCache() {
    cachedPlans = null;
    cacheTimestamp = 0;
  },
};

export const formatCurrency = (value, currency = 'USD') => {
  if (typeof value !== 'number') {
    return value || '—';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    console.warn('Currency format fallback:', error);
    return `${currency} ${value.toFixed(2)}`;
  }
};

export const groupPlansByCycle = (plans = []) => {
  // Sort plans by amount (ascending - lowest first)
  const sortedPlans = [...plans].sort((a, b) => {
    // Get the price for comparison (prefer monthlyPrice, then yearlyPrice, then price)
    const getPrice = (plan) => {
      if (typeof plan.monthlyPrice === 'number') return plan.monthlyPrice;
      if (typeof plan.yearlyPrice === 'number') return plan.yearlyPrice;
      return plan.price || 0;
    };
    
    const priceA = getPrice(a);
    const priceB = getPrice(b);
    
    // Sort ascending (lower price first)
    return priceA - priceB;
  });
  
  return sortedPlans.reduce((acc, plan) => {
    const cycle = (plan.billingCycle || 'monthly').toLowerCase();
    if (!acc[cycle]) {
      acc[cycle] = [];
    }
    acc[cycle].push(plan);
    return acc;
  }, {});
};

// Get user's active subscription
export const getActiveSubscription = async (token) => {
  try {
    const response = await axios.get('/api/subscription/user-subscription', {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching active subscription:', error);
    throw error;
  }
};

// Create Stripe checkout session
export const createCheckoutSession = async (token, productId, billingCycle) => {
  try {
    const response = await axios.post(
      '/api/subscription/create-checkout-session',
      {
        product_id: productId,
        billing_cycle: billingCycle,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

